import type { QuickFilter } from "@amzn/ads-blink-client";
import {
    Button,
    Flex,
    IconButton,
    SplitButton,
    TextButton,
    Text,
} from "@amzn/storm-ui";
import { pencil } from "@amzn/storm-ui-icons";
import { useMountEffect } from "@react-hookz/web";
import type { ReactElement } from "react";
import React, { useState } from "react";
import { useStore } from "zustand";

import { TELEMETRY_ID } from "../../../../constants/telemetry";
import useEncodedUnifiedFilterStateUrl from "../../../../hooks/misc/useEncodedUnifiedFilterStateUrl";
import {
    notificationStore,
    NotificationType,
} from "../../../../store/notification";
import type { PartialUnifiedfilterState } from "../../../../utils/filters.utils";
import { getProcessedInitialUnifiedFilterState } from "../../../../utils/filters.utils";
import { logWithTakt } from "../../../../utils/telemetry.utils";

import { QuickFilterManagementModal } from "./QuickFilterManagementModal";
import { SearchQuickFilterActionAsync } from "./useSearchQuickFilterAction";

export const QuickFilterDropdown = (): ReactElement => {
    const [isModalOpen, setIsModalOpen] = useState(false);

    const [quickFilterUnderManagement, setQuickFilterUnderManagement] =
        useState<QuickFilter>();

    const [quickFilters, setQuickFilters] = useState<QuickFilter[]>([]);

    const [_, searchQuickFiltersAction] = SearchQuickFilterActionAsync();

    const [setEncodedUnifiedFilterStateUrl] = useEncodedUnifiedFilterStateUrl();

    const [upsertNotification] = useStore(notificationStore, (state) => [
        state.upsertNotification,
    ]);

    // On component load, retrieve our quick filters
    useMountEffect(async () => {
        void fetchQuickFilters();
    });

    const openManagementModal = (quickFilter?: QuickFilter) => {
        setQuickFilterUnderManagement(quickFilter);
        setIsModalOpen(true);
    };

    /**
     * Retrieves the user's quick filters for render
     */
    const fetchQuickFilters = async () => {
        try {
            const response = await searchQuickFiltersAction.execute();
            setQuickFilters(response.data ?? []);
        } catch (error) {
            console.error("Error fetching quick filters", error);
        }
    };

    /**
     * Performs the logic needed to load the filter into the filter store
     */
    const loadFilter = (filter: QuickFilter) => {
        let parsed: PartialUnifiedfilterState | undefined;
        try {
            parsed = filter.filter ? JSON.parse(filter.filter) : undefined;
        } catch (error) {
            console.error("Error parsing quick filter", error);
            upsertNotification(
                "Failed to parse filter when loading. Please try again, or contact support.",
                NotificationType.ERROR,
            );
            return;
        }
        const state = getProcessedInitialUnifiedFilterState(parsed);
        if (state) {
            setEncodedUnifiedFilterStateUrl(state);
        }

        logWithTakt(TELEMETRY_ID.QUICK_FILTER.APPLY, {
            id: filter.identifier,
        });
    };

    /**
     * Opens the save filter modal
     */
    const saveFilter = () => {
        openManagementModal();
    };

    const editFilter = (e: React.MouseEvent, filter: QuickFilter) => {
        e.stopPropagation();
        openManagementModal(filter);
    };

    return (
        <>
            <QuickFilterManagementModal
                isModalOpen={isModalOpen}
                setIsModalOpen={setIsModalOpen}
                handleSubmit={() => void fetchQuickFilters()}
                quickFilter={quickFilterUnderManagement}
            />

            <SplitButton
                mainButtonLabel="Save Filters"
                mainButtonOnClick={saveFilter}
            >
                {quickFilters.length === 0 && (
                    <div style={{ maxWidth: 200, textAlign: "center" }}>
                        <Text withMargin>
                            Your saved quick filters will show here.
                        </Text>
                        <Text>
                            Click{" "}
                            <TextButton
                                onClick={saveFilter}
                                data-testid="save-quickfilter-when-empty"
                            >
                                here
                            </TextButton>
                            , or the save button above, to save your first one!
                        </Text>
                    </div>
                )}
                {quickFilters.map((filter) => (
                    <Flex
                        justifyContent="space-between"
                        alignItems="center"
                        columnGap="xxxsmall"
                    >
                        <Button
                            variant="transparent"
                            small={true}
                            style={{
                                flexGrow: 1,
                                justifyContent: "flex-start",
                            }}
                            onClick={() => loadFilter(filter)}
                        >
                            {filter.name}
                        </Button>

                        <IconButton
                            icon={pencil}
                            label={"Edit Quick Filter"}
                            variant="transparent"
                            onClick={(e) => editFilter(e, filter)}
                            data-testid={`edit-quick-filter-${filter.identifier}`}
                        />
                    </Flex>
                ))}
            </SplitButton>
        </>
    );
};
